import React from 'react'
import {
    MdCheckCircle,
    MdClose,
    MdHelpOutline,
    MdMusicNote,
    MdOutlineSchool,
    MdPeopleAlt,
    MdPersonAdd,
    MdRefresh,
    MdWbSunny,
} from 'react-icons/md'
import SummerBreakDriveTimes from './SummerBreakDriveTimes'
import SummerBreakReplacementPlanner from './SummerBreakReplacementPlanner'
import './summer-break-summary-modal.css'

const getTeacherPlanIcon = (teacherPlan) => {
    if (teacherPlan === 'needs-new-teacher') return MdPersonAdd
    if (teacherPlan === 'continuing') return MdCheckCircle
    return MdHelpOutline
}

const SummerBreakSummaryModal = ({
    open,
    loading,
    error,
    summary,
    onClose,
    onRetry,
}) => {
    React.useEffect(() => {
        if (!open) return undefined

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose()
        }

        document.addEventListener('keydown', closeOnEscape)
        return () => document.removeEventListener('keydown', closeOnEscape)
    }, [onClose, open])

    if (!open) return null

    return (
        <div
            className='summer-break-summary__backdrop'
            onMouseDown={(event) => event.target === event.currentTarget && onClose()}
        >
            <section
                className='summer-break-summary'
                role='dialog'
                aria-modal='true'
                aria-labelledby='summer-break-summary-title'
            >
                <header className='summer-break-summary__header'>
                    <div className='summer-break-summary__heading'>
                        <span className='summer-break-summary__sun' aria-hidden='true'>
                            <MdWbSunny size={25} />
                        </span>
                        <div>
                            <h2 id='summer-break-summary-title'>Summer Break Summary</h2>
                            <p>Students who will need lesson schedules revisited.</p>
                        </div>
                    </div>
                    <button
                        type='button'
                        className='summer-break-summary__close'
                        aria-label='Close summer break summary'
                        onClick={onClose}
                    >
                        <MdClose size={22} />
                    </button>
                </header>

                {loading ? (
                    <div className='summer-break-summary__state' role='status'>
                        <span className='summer-break-summary__spinner' />
                        Gathering summer break students…
                    </div>
                ) : error ? (
                    <div className='summer-break-summary__state summer-break-summary__state--error' role='alert'>
                        <strong>We couldn’t load the summary.</strong>
                        <span>{error}</span>
                        <button type='button' onClick={onRetry}>
                            <MdRefresh size={17} /> Try again
                        </button>
                    </div>
                ) : (
                    <div className='summer-break-summary__content'>
                        <div className='summer-break-summary__totals'>
                            <div className='summer-break-summary__total-card is-students'>
                                <MdPeopleAlt size={21} aria-hidden='true' />
                                <div>
                                    <strong>{summary?.totalStudents || 0}</strong>
                                    <span>Students on break</span>
                                </div>
                            </div>
                            <div className='summer-break-summary__total-card is-schools'>
                                <MdOutlineSchool size={21} aria-hidden='true' />
                                <div>
                                    <strong>{summary?.schoolsWithStudents || 0}</strong>
                                    <span>Schools affected</span>
                                </div>
                            </div>
                            <div className='summer-break-summary__total-card is-instruments'>
                                <MdMusicNote size={21} aria-hidden='true' />
                                <div>
                                    <strong>{summary?.totalInstrumentPlacements || 0}</strong>
                                    <span>Instrument placements</span>
                                </div>
                            </div>
                        </div>

                        <div className='summer-break-summary__teacher-plans'>
                            <div className='summer-break-summary__teacher-plan-total is-needs-new-teacher'>
                                <MdPersonAdd size={19} aria-hidden='true' />
                                <strong>{summary?.needsNewTeacher || 0}</strong>
                                <span>Need a new teacher</span>
                            </div>
                            <div className='summer-break-summary__teacher-plan-total is-continuing'>
                                <MdCheckCircle size={19} aria-hidden='true' />
                                <strong>{summary?.continuingWithTeacher || 0}</strong>
                                <span>Continuing with current teacher</span>
                            </div>
                            <div className='summer-break-summary__teacher-plan-total is-needs-review'>
                                <MdHelpOutline size={19} aria-hidden='true' />
                                <strong>{summary?.teacherNeedsReview || 0}</strong>
                                <span>Teacher needs review</span>
                            </div>
                        </div>

                        <div className='summer-break-summary__schools'>
                            {(summary?.schools || []).map((school) => (
                                <article
                                    className={`summer-break-summary__school ${school.totalStudents === 0 ? 'is-empty' : ''}`}
                                    key={school.id}
                                >
                                    <div className='summer-break-summary__school-header'>
                                        <div>
                                            <span>{school.id.toUpperCase()}</span>
                                            <h3>{school.name}</h3>
                                            <div className='summer-break-summary__school-needs-teacher'>
                                                <MdPersonAdd size={13} aria-hidden='true' />
                                                {school.needsNewTeacher || 0}{' '}
                                                {(school.needsNewTeacher || 0) === 1
                                                    ? 'student needs a new teacher'
                                                    : 'students need a new teacher'}
                                            </div>
                                        </div>
                                        <div className='summer-break-summary__school-count'>
                                            <strong>{school.totalStudents}</strong>
                                            <span>{school.totalStudents === 1 ? 'student' : 'students'}</span>
                                        </div>
                                    </div>

                                    {school.instruments.length === 0 ? (
                                        <div className='summer-break-summary__empty-school'>
                                            No students on summer break
                                        </div>
                                    ) : (
                                        <div className='summer-break-summary__instrument-list'>
                                            {school.instruments.map((instrument) => (
                                                <div className='summer-break-summary__instrument' key={instrument.name}>
                                                    <div className='summer-break-summary__instrument-heading'>
                                                        <span className='summer-break-summary__music-icon' aria-hidden='true'>
                                                            <MdMusicNote size={15} />
                                                        </span>
                                                        <strong>{instrument.name}</strong>
                                                        <span className='summer-break-summary__instrument-count'>
                                                            {instrument.count}
                                                        </span>
                                                    </div>
                                                    <div className='summer-break-summary__student-list'>
                                                        {instrument.students.map((student) => {
                                                            const TeacherPlanIcon = getTeacherPlanIcon(student.teacherPlan)

                                                            return (
                                                                <div
                                                                    className={`summer-break-summary__student is-${student.teacherPlan || 'needs-review'}`}
                                                                    key={`${instrument.name}-${student.studentId}`}
                                                                    title={student.teacherPlanLabel}
                                                                >
                                                                    <TeacherPlanIcon size={14} aria-hidden='true' />
                                                                    <span>
                                                                        <strong>
                                                                            {student.studentName}
                                                                            {student.familyName && student.familyName !== student.studentName
                                                                                ? ` (${student.familyName})`
                                                                                : ''}
                                                                        </strong>
                                                                        <small>{student.teacherPlanLabel}</small>
                                                                    </span>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                        <SummerBreakReplacementPlanner summary={summary} />
                        <SummerBreakDriveTimes summary={summary} />
                    </div>
                )}
            </section>
        </div>
    )
}

export default SummerBreakSummaryModal
